import React, { useEffect, useState, useMemo } from 'react';
import './JokeAnalysisHeader.css';
import { 
  countTotalNumOfPunchlines, 
  countTotalNumOfTaglines, 
  countTotalSetupLengthOfPerformance, 
  countTotalPunchlineLengthOfPerformance,
  countTotalTaglineLengthOfPerformance 
} from './JsonTextAnalysis';
import LoadingBarPercentageSetupPunchLaughs from './LoadingBarPercentageSetupPunchLaughs';
import CurrentGraph from './currentGraph';
import Tooltip from '@mui/material/Tooltip';

const JokeAnalysisHeader = ({ jokes = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // State variables to store aggregated data
  const [totalPunchlines, setTotalPunchlines] = useState(0);
  const [totalTaglines, setTotalTaglines] = useState(0);
  const [totalWords, setTotalWords] = useState(0);
  const [numOfLaughs, setNumOfLaughs] = useState(0);
  const [biggestLaugh, setBiggestLaugh] = useState(0);
  const [formattedTotalTime, setFormattedTotalTime] = useState('');
  const [totalTimeInSetups, setTotalTimeInSetups] = useState(0);
  const [totalTimeLaughing, setTotalTimeLaughing] = useState(0);
  const [speakingRate, setSpeakingRate] = useState(0);
  const [laughScore, setLaughScore] = useState(0);
  const [laughQualityScore, setLaughQualityScore] = useState('0%');
  const [audienceResponseArray, setAudienceResponseArray] = useState([]);
  const [totalSetupLength, setTotalSetupLength] = useState(0);
  const [totalPunchlineLength, setTotalPunchlineLength] = useState(0);
  const [totalTaglineLength, setTotalTaglineLength] = useState(0);

  // useEffect to handle data aggregation and state updates
  useEffect(() => {
    if (!Array.isArray(jokes) || jokes.length === 0) return;

    // Sanitize jokes data to avoid undefined values 
    const sanitizedJokes = jokes.map(joke => ({
      setup: Array.isArray(joke.setup) ? joke.setup : [''],
      punchline: joke.punchline?.text ? joke.punchline : { text: '', response: [] },
      taglines: Array.isArray(joke.taglines) ? joke.taglines : []
    }));

    // Aggregate punchlines, taglines, and word counts
    const punchlines = countTotalNumOfPunchlines(sanitizedJokes);
    const taglines = countTotalNumOfTaglines(sanitizedJokes);
    const setupLength = countTotalSetupLengthOfPerformance(sanitizedJokes);
    const punchlineLength = countTotalPunchlineLengthOfPerformance(sanitizedJokes);
    const taglineLength = countTotalTaglineLengthOfPerformance(sanitizedJokes);
    const words = setupLength + punchlineLength + taglineLength;

    // Flatten audience responses across all jokes
    const audienceResponses = sanitizedJokes.flatMap(joke => {
      const punchlineResponses = joke.punchline.response || [];
      const taglineResponses = joke.taglines.flatMap(tagline => tagline.punchline.response || []);
      return [...punchlineResponses, ...taglineResponses];
    });

    // Calculate total times and scores
    const totalTimeBeforeLaugh = audienceResponses.filter(response => response === 0).length;
    const totalTimeLaughing = audienceResponses.filter(response => response !== 0).length;
    const totalTime = totalTimeBeforeLaugh + totalTimeLaughing;
    const totalLaughScore = audienceResponses.reduce((sum, value) => sum + value, 0);
    const maxScore = audienceResponses.length * 10;
    const laughQualityPercentage = maxScore > 0 ? ((totalLaughScore / maxScore) * 100).toFixed(1) : '0';

    // Update state in one go
    setAudienceResponseArray(audienceResponses);
    setBiggestLaugh(Math.max(...audienceResponses, 0)); // Defaults to 0 if no values
    setLaughScore(totalLaughScore);
    setLaughQualityScore(`${laughQualityPercentage}%`);
    setTotalPunchlines(punchlines);
    setTotalTaglines(taglines);
    setNumOfLaughs(punchlines + taglines);
    setTotalWords(words);
    setTotalTimeInSetups(totalTimeBeforeLaugh);
    setTotalTimeLaughing(totalTimeLaughing);
    setSpeakingRate(totalTimeBeforeLaugh > 0 ? (words / totalTimeBeforeLaugh) * 60 : 0);
    setFormattedTotalTime(`${Math.floor(totalTime / 60)}m ${totalTime % 60}s`);

    // Update setup, punchline, and tagline lengths
    setTotalSetupLength(setupLength);
    setTotalPunchlineLength(punchlineLength);
    setTotalTaglineLength(taglineLength);
  }, [jokes]);


  // Calculate percentages
  const setupPercentage = totalWords > 0 ? ((totalSetupLength / totalWords) * 100).toFixed(0) : 0;
  const punchlinePercentage = totalWords > 0 ? ((totalPunchlineLength / totalWords) * 100).toFixed(0) : 0;
  const taglinePercentage = totalWords > 0 ? ((totalTaglineLength / totalWords) * 100).toFixed(0) : 0;
  const percentageOfLaughterInPerformance = totalTimeInSetups + totalTimeLaughing > 0
    ? ((totalTimeLaughing / (totalTimeInSetups + totalTimeLaughing)) * 100).toFixed(1)
    : 0;

    return (
        <div className={`joke-analysis-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {/* The button to expand or collapse */}
        <button className="collapse-button" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Hide' : 'View Performance Stats'}
        </button>
  
        {/* Conditionally render the performance stats if expanded */}
        {isExpanded && (
          <div className="analysis-content">
            <div className="performance-analysis-top">
              <div className="performance-analysis-words">
                <Tooltip title="Total number of words spoken in the performance">
                  <p>Total Words: <strong>{totalWords}</strong></p>
                </Tooltip>
                <Tooltip title="Total number of words in the setups of the performance">
                  <p>Setups: <strong>{totalSetupLength}</strong> words - <strong>{setupPercentage}%</strong></p>
                </Tooltip>
                <Tooltip title="Total number of words in the punchlines of the performance">
                  <p>Punchlines: <strong>{totalPunchlineLength}</strong> words - <strong>{punchlinePercentage}%</strong></p>
                </Tooltip>
                <Tooltip title="Total number of words in the taglines of the performance">
                  <p>Taglines: <strong>{totalTaglineLength}</strong> words - <strong>{taglinePercentage}%</strong></p>
                </Tooltip>
                <Tooltip title={`Laughs: ${numOfLaughs} (Punchlines: ${totalPunchlines}, Taglines: ${totalTaglines})`}>
                  <p>Laughs: <strong>{numOfLaughs}</strong></p>
                </Tooltip>
                <Tooltip title={`Biggest laugh: ${biggestLaugh}`}>
                  <p>Biggest Laugh: <strong>{biggestLaugh}</strong></p>
                </Tooltip>
                <Tooltip title="The overall laugh score for the performance">
                  <p>Laugh Score: <strong>{laughScore}</strong></p>
                </Tooltip>
                <Tooltip title="Laugh quality score percentage">
                  <p>Laugh Quality Score: <strong>{laughQualityScore}</strong></p>
                </Tooltip>
              </div>
  
              <div className="performance-analysis-time">
                <Tooltip title="Total time the performance lasted">
                  <p>Total Time: <strong>{formattedTotalTime}</strong></p>
                </Tooltip>
                <Tooltip title="Time spent with the comedian talking">
                  <p>Comedian Talking: {totalTimeInSetups} sec</p>
                </Tooltip>
                <Tooltip title="Time spent with the audience laughing">
                  <p>Laughter: {totalTimeLaughing} sec | <strong>{percentageOfLaughterInPerformance}%</strong></p>
                </Tooltip>
                <Tooltip title="Speaking rate (words per minute)">
                  <p>Speaking Rate: <strong>{speakingRate.toFixed(1)} WPM</strong></p>
                </Tooltip>
              </div>
            </div>
  
            <div className="mini-graph-area">
              <LoadingBarPercentageSetupPunchLaughs 
                setupPercentage={setupPercentage} 
                punchlinePercentage={punchlinePercentage} 
                taglinePercentage={taglinePercentage} 
                laughPercentage={percentageOfLaughterInPerformance} 
              />
              <div className="mini-graph-container">
                <CurrentGraph data={audienceResponseArray} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  export default JokeAnalysisHeader;